import React, { useState, useEffect, useRef } from 'react';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import CustomizableSnackbar from '../../components/Snackbar/Snackbar';
import Copyright from '../../components/Copyright/Copyright';
import { signUp, signInWithProvider, getUser } from '../../api/usersAPI';
import { getHalls } from '../../api/hallsAPI';
import { ButtonGroup } from '@material-ui/core';
import { getErrorMessage } from '../../tools/messages';
import {
  Facebook,
  Twitter,
  GitHub,
} from '@material-ui/icons';

function Registered() {
  return (
    <div>
      <br />
      <Typography color="textSecondary" align="center">
        Thanks for registering! We sent you an email to setup a password. 
        An email has been sent to the administrator(s) of the directory.
        If accepted, you will be able to access the directory.
        
        Thanks for using the Gospel Hall Directory!
      </Typography>
      <br />
      <Button
        onClick={() => window.location = '/signin'}
        variant="contained"
        color="primary"
      >
        Back
      </Button>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    width: '100%', // Fix IE 11 issue.
  },
}));

export default function SignUp({ ...props }) {
  const classes = useStyles();
  const [hall, setHall] = useState();
  const [halls, setHalls] = useState([]);
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [checkEmail, setCheckEmail] = useState();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarVariant, setSnackbarVariant] = useState('info');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const nameRegex = new RegExp("^[a-zA-Z]+$");

  useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth);
    const initializeHalls = async () => {
      const halls = await getHalls();
      setHalls(halls);
    };
    initializeHalls();
  }, []);

  const handleSignIn = () => {
    props.history.push('/signin');
  }
  const handleSubmit = async () => {
    if (!isValidated()) {
      setSnackbarVariant('error');
      setSnackbarMessage('Input did not pass validation.');
      setIsSnackbarOpen(true);
      return;
    }
    const user = {
      firstName,
      lastName,
      email,
      hall: hall.id,
    };
    try {
      await signUp(user);
      setIsRegistered(true);
      setSnackbarVariant('success');
      setSnackbarMessage(`You have successfully requested access to ${hall.name}.`);
      setIsSnackbarOpen(true);
    } catch (e) {
      setSnackbarVariant('error');
      setSnackbarMessage('Unable to complete registration. Please try again later.');
      setIsSnackbarOpen(true);
    }
      
  }
  const handleProviderSignUp = async (provider) => {
    try {
      const userCredential = await signInWithProvider(provider);
      const { email } = userCredential.user;
      const user = await getUser();
      const {
        isPending,
        isRejected,
        hall,
        role,
      } = user;
      if (isPending) {
        props.history.push('/pending');
      } else if (!isPending && isRejected) {
        props.history.push('/rejected');
      } else if (!hall || !role) {
        props.history.push({
          pathname: '/provider/signup',
          state: { user: { email, id: user.id }}
        });
      }
    } catch (e) {
      const { code } = e;
      const message = getErrorMessage(code);
      setSnackbarVariant('error');
      setSnackbarMessage(message);
      setIsSnackbarOpen(true);
    }
  }
  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const isValidated = () => {
    const isFilledIn = firstName && lastName && email && checkEmail && hall;
    const emailsMatch = email === checkEmail;
    const firstNameCheck = nameRegex.test(firstName);
    const lastNameCheck = nameRegex.test(lastName);
    return isFilledIn && emailsMatch && firstNameCheck && lastNameCheck;   
  }
  return (
    <Container component="main" maxWidth="xs">
      <CustomizableSnackbar
        open={isSnackbarOpen}
        handleClose={handleSnackbarClose}
        variant={snackbarVariant}
        message={snackbarMessage}
      />
      <CssBaseline />
      {!isRegistered &&
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        <form className={classes.form} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="fname"
                variant="outlined"
                required
                fullWidth
                value={firstName}
                helperText={!nameRegex.test(firstName) && "Only alphabetical characters allowed."}
                error={!nameRegex.test(firstName)}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                required
                fullWidth
                label="Last Name"
                value={lastName}
                helperText={!nameRegex.test(lastName) && "Only alphabetical characters allowed."}
                error={!nameRegex.test(lastName)}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="lname"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl 
                required
                className={classes.formControl}
                variant="outlined"
              >
                <InputLabel ref={inputLabel}>Gospel Hall</InputLabel>
                <Select
                  labelId="outlined-label"
                  value={hall}
                  onChange={(e) => setHall(e.target.value)}
                  labelWidth={labelWidth}
                >
                  {halls.map((hall) => <MenuItem key={hall.id} value={hall}>{hall.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                required
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                required
                fullWidth
                error={email !== checkEmail}
                label="Retype Email Address"
                value={checkEmail}
                onChange={(e) => setCheckEmail(e.target.value)}
              />
            </Grid>
          </Grid>
          <Button
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={!isValidated()}
          >
            {"Sign Up"}
          </Button>
          <Grid container justify="flex-end">
            <Grid item>
              <Link onClick={handleSignIn} variant="body2">
                {"Already have an account? Sign in"}
              </Link>
            </Grid>
          </Grid>
          <br />
            {"Or sign up with one of the following providers"}
          <br />
          <br />
          <Grid container>
              <Grid item xs>
              <ButtonGroup color="primary" aria-label="outlined primary button group">
                <Button onClick={() => handleProviderSignUp('google') }>
                  {"Google"}
                </Button>
                <Button onClick={() => handleProviderSignUp('facebook') }>
                  <Facebook />
                </Button>
                <Button onClick={() => handleProviderSignUp('twitter') }>
                  <Twitter />
                </Button>
                <Button onClick={() => handleProviderSignUp('github') }>
                  <GitHub />
                </Button>
              </ButtonGroup>
              </Grid>
            </Grid>
        </form>
      </div>
      }
      <br />
      {isRegistered && Registered()}
      <Box mt={5}>
        <Copyright />
      </Box>
    </Container>
  );
}
